import { NavigateFunction } from 'react-router-dom';
import styled from 'styled-components';
import { Environment, getEnvironment } from '../../../../common/src/environment.ts';
import {
  HouseholdOriginSource,
  HouseholdSurveyResponses,
  OrgType,
  StripeSubscriptionLevel,
} from '../../../../common/src/model.ts';
import { getOnboardingPath, OnboardingPages } from '../../../../common/src/pages.ts';
import * as server_api from '../../../../common/src/server_api.ts';
import { ensureUnreachable } from '../../../../common/src/util.ts';
import {
  getAllLocalStorageKeys,
  localStorageGet,
  LocalStorageKey,
  localStorageRemove,
  localStorageSet,
  OnboardingLocalStoragePrefix,
} from '../../data/client_local_storage.ts';
import { synchronizeHouseholdWithServer } from '../../data/use_household.tsx';
import {
  isInsideAppleAppStoreApp,
  isInsideGooglePlayApp,
  isInsideMobileApp,
  isInsideMobileBrowser,
  launchStripeCheckout,
} from '../../util/billing.ts';
import { alert } from '../../util/confirm.tsx';
import { getUrlForPage } from '../../util/path.ts';
import { Device, getDevice } from '../../util/platform.ts';
import { withSearchParams } from '../../util/use_location.tsx';
import {
  nextOnboardingStep,
  OnboardingContext,
  OnboardingDestination,
  OnboardingEvent,
  OnboardingParam,
  OnboardingPlatform,
} from './onboarding_flow.ts';

const musicLeaderMessage =
  'Hello,\n' +
  '\n' +
  'This app makes the church’s music more accessible.  Check out the app here: https://singyourpart.org\n' +
  '\n' +
  "Click the link below to set a 20 minute phone call with their team located in Boise and they'll go over what it would look like for the church. Thank you!\n" +
  '\n' +
  'https://calendar.app.google/cn23rz2diHASqKon9';

export async function shareWithMusicLeader() {
  // *** Note that this only works over https in iOS Safari ***
  await shareIgnoringCancel({
    ...(getDevice() !== Device.iOS && { title: musicLeaderMessage }), // prefer the title
    ...(getDevice() === Device.iOS && { text: musicLeaderMessage }), // fall back on the text for Safari
  });
}

// Dismissing the share sheet rejects with an AbortError. That is the user's choice, not a failure:
// letting it escape left an uncaught rejection from the subscription banner's Invite Your Church
// button and stopped the onboarding share page from moving on (GitHub issue #3551).
async function shareIgnoringCancel(data: ShareData) {
  try {
    await navigator.share(data);
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') {
      return;
    }
    throw e;
  }
}

export const Title = styled.div`
  // TODO(hewitt): Use Bold instead of SemiBold
  font-family: Jost-SemiBold, sans-serif;
  font-size: 2.7em;
  color: var(--color-text-button-onboarding);
  padding-bottom: 0.5em;
  text-wrap: nowrap;
`;

export const Message = styled.div<{
  $widthInChars: number;
  $lessPadding?: boolean;
  $moreTopPadding?: boolean;
  $noPadding?: boolean;
}>`
  font-size: 1.3em;
  width: ${(props) => props.$widthInChars}ch;
  padding-bottom: ${(props) => (props.$noPadding ? '0' : props.$lessPadding ? '0.5em' : '1.5em')};
  ${(props) => props.$moreTopPadding && `padding-top: 4em`}
`;

export const BoldMessage = styled.span`
  font-family: Jost-SemiBold, sans-serif;
`;

export const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: 0.3em;
  overflow: scroll;
`;

export const ButtonContent = styled(Content)`
  padding-top: 10px;
  padding-bottom: 10px;
  gap: 10px;
`;

export const TextInput = styled.input<{
  $width?: string;
  $marginBottom?: string;
  $isError?: boolean;
}>`
  font-family: Jost-SemiBold, sans-serif;
  font-size: 1.2em;
  border: none;
  border-bottom: 1px solid var(--color-text);
  text-align: center;
  color: ${(props) => (props.$isError ? 'var(--color-invalid-input)' : 'var(--color-text)')};
  background-color: transparent;
  width: ${(props) => (props.$width ? props.$width : '20ch')};
  margin-bottom: ${(props) => (props.$marginBottom ? props.$marginBottom : 'none')};
`;

export function clearOnboardingLocalStorage() {
  for (const key of getAllLocalStorageKeys()) {
    if (key.startsWith(OnboardingLocalStoragePrefix)) {
      localStorageRemove(key as LocalStorageKey);
    }
  }
}

export const SubscribeContextQueryString = OnboardingParam.Subscribe;
// the promotional campaign slug, e.g. family-worship (GitHub issue #3551)
export const PromoCampaignQueryString = OnboardingParam.Promo;
export const EditAllHouseholdInfoQueryString = OnboardingParam.EditAll;
// Retired (OrgIdTokenQueryString supersedes it) but kept for good: invite links already sent by
// email still carry it, and links in the outside world never expire (backward-compatibility skill).
export const ChurchIdTokenQueryString = 'church-id';
export const OrgIdTokenQueryString = 'org-id';
// Retired (OrgAdminTokenQueryString supersedes it) but kept for good, like ChurchIdTokenQueryString.
export const ChurchAdminTokenQueryString = 'church-admin-token';
export const OrgAdminTokenQueryString = 'org-admin-token';
export const RoleTokenQueryString = 'role';
export const ActivateSearchQueryString = 'activate-search';
export const FromHouseholdJoinQueryString = OnboardingParam.FromHouseholdJoin;
export const FromChooseUserQueryString = OnboardingParam.FromChooseUser;

// event island
export const OrganizationQueryString = 'organization';
export const DateQueryString = 'date';
export const SongListTypeQueryString = 'song-list-type';
export const IntervalTypeQueryString = 'interval-type';

// automatic sign in
export const HouseholdEmailQueryString = 'household-email';

export function isSubscribing(): boolean {
  const params = new URLSearchParams(window.location.search);
  return params.has(SubscribeContextQueryString);
}

export function isAdminInvite(): boolean {
  const params = new URLSearchParams(window.location.search);
  return params.has(OrgAdminTokenQueryString) || params.has(ChurchAdminTokenQueryString);
}

export function isHouseholdInvite(): boolean {
  const params = new URLSearchParams(window.location.search);
  return (
    (params.has(OrgIdTokenQueryString) || params.has(ChurchIdTokenQueryString)) &&
    !params.has(OrgAdminTokenQueryString) &&
    !params.has(ChurchAdminTokenQueryString)
  );
}

export function editAllHouseholdFields(): boolean {
  const params = new URLSearchParams(window.location.search);
  return params.has(EditAllHouseholdInfoQueryString);
}

export function activateSearch(): boolean {
  const params = new URLSearchParams(window.location.search);
  return params.has(ActivateSearchQueryString);
}

export async function navigateToOnboardingPage(
  navigate: NavigateFunction,
  page: OnboardingPages,
  ...additionalParams: string[]
) {
  await navigate(withSearchParams(getOnboardingPath(page), ...additionalParams));
}

// The flow's context as it stands now: the query parameters, the household in local storage and the
// platform the page runs on
export function getOnboardingContext(): OnboardingContext {
  const params = new URLSearchParams(window.location.search);
  return {
    subscribing: isSubscribing(),
    adminInvite: isAdminInvite(),
    householdInvite: isHouseholdInvite(),
    editAll: editAllHouseholdFields(),
    fromHouseholdJoin: params.has(FromHouseholdJoinQueryString),
    household: localStorageGet(LocalStorageKey.Household),
    platform: getOnboardingPlatform(),
    promo: Boolean(getRedeemablePromoCampaignSlug()),
  };
}

// The promotional campaign slug on the current onboarding URL, if any (GitHub issue #3551). It arrives
// on the landing URL and withSearchParams carries it through every onboarding page.
export function getPromoCampaignSlug(search: string = window.location.search): string | undefined {
  return new URLSearchParams(search).get(PromoCampaignQueryString) || undefined;
}

// The promotional campaign slug when it can be redeemed here: in a browser only, because inside the
// store apps subscribing goes through Apple or Google billing (GitHub issue #3551).
export function getRedeemablePromoCampaignSlug(search: string = window.location.search): string | undefined {
  return isInsideMobileApp() ? undefined : getPromoCampaignSlug(search);
}

// Opens Stripe Checkout for the household. Returns false when it could not be opened, after telling the
// household why: the server explains a promotion it declines (GitHub issue #3551).
export async function startStripeCheckout(
  householdToken: string,
  level: StripeSubscriptionLevel,
  promo?: string,
): Promise<boolean> {
  try {
    await launchStripeCheckout({ householdToken, level, promo });
    return true;
  } catch (e: any) {
    if (e instanceof server_api.CheckoutRejectedError) {
      await alert({ confirmation: <span>{e.message}</span> });
      return false;
    }
    console.error(`Failed to launch Stripe checkout: ${e.message}`);
    await alert({ confirmation: <span>Network error - please try again</span> });
    return false;
  }
}

function getOnboardingPlatform(): OnboardingPlatform {
  if (isInsideAppleAppStoreApp()) {
    return OnboardingPlatform.IOSApp;
  }
  if (isInsideGooglePlayApp()) {
    return OnboardingPlatform.AndroidApp;
  }
  return isInsideMobileBrowser() ? OnboardingPlatform.MobileBrowser : OnboardingPlatform.DesktopBrowser;
}

export async function goToOnboardingDestination(navigate: NavigateFunction, destination: OnboardingDestination) {
  switch (destination.kind) {
    case 'onboarding':
      await navigateToOnboardingPage(navigate, destination.page, ...(destination.addParams ?? []));
      return;
    case 'invite':
      await navigate(withSearchParams(getUrlForPage(destination.page), ...(destination.addParams ?? [])));
      return;
    case 'app':
      await finishOnboarding();
      await navigate(getUrlForPage(destination.page), destination.replace ? { replace: true } : undefined);
      return;
    case 'home':
      await finishOnboarding();
      await navigate('/');
      return;
    case 'promoCheckout': {
      const household = localStorageGet(LocalStorageKey.Household);
      const promo = getRedeemablePromoCampaignSlug();
      if (
        household?.token &&
        promo &&
        (await startStripeCheckout(household.token, StripeSubscriptionLevel.Annual, promo))
      ) {
        return;
      }
      // checkout did not start (the reason was shown): the offer page lets the household try again
      await navigateToOnboardingPage(navigate, OnboardingPages.SubscribePayment);
      return;
    }
    case 'createHousehold':
      // only the last-name page creates directly; anywhere else, ask on the confirmation page
      await navigateToOnboardingPage(navigate, OnboardingPages.HouseholdCreate);
      return;
    default:
      ensureUnreachable(destination);
  }
}

// Decides where `event` leads (see onboarding_flow.ts) and goes there
export async function goToNextOnboardingStep(navigate: NavigateFunction, event: OnboardingEvent) {
  await goToOnboardingDestination(navigate, nextOnboardingStep(event, getOnboardingContext()));
}

export function isDebugOnboardingEnvironment(): boolean {
  // iOS has a test app (TestFlight) that runs against test.singyourpart.app, so it is not considered a "debug" env
  // Android does not, so we consider test.singyourpart.app to be a debug env so that we can test onboarding for Android
  return (
    getEnvironment() === Environment.Dev ||
    (getDevice() === Device.Android && window.location.hostname.startsWith('test'))
  );
}

// Sends the survey answers gathered during onboarding (local storage) to the household in local storage, then
// clears onboarding's local storage.  Household welcome calls it on opening, since it also initializes the new
// household's survey responses and church; every other ending goes through finishOnboarding.
export async function recordSurveyResponses() {
  const household = localStorageGet(LocalStorageKey.Household);
  if (!household?.token) {
    clearOnboardingLocalStorage();
    return;
  }
  const responses = localStorageGet(LocalStorageKey.OnboardingHouseholdSurveyResponses);
  if (Object.keys(responses).length > 0) {
    // don't store an empty object in testing
    await server_api.recordHouseholdSurveyResponses({
      householdToken: household.token,
      // the server replaces the responses, so keep the household's earlier answers (e.g. a household joined)
      responses: { ...household.surveyResponses, ...responses },
    });
  }
  await synchronizeHouseholdWithServer({ force: true }); // initializes household.surveyResponses, church
  clearOnboardingLocalStorage();
}

// Records the survey answers still pending, if any, and clears onboarding's local storage, so the answers reach the
// server whichever path the visitor took: invites, subscribing, a new household.  Every way out of onboarding into the
// app passes here (goToOnboardingDestination), and the pages where onboarding ends - download, the invite pages - call
// it on finishing, since a visitor may leave them without clicking through.  With no answers pending there is nothing
// to send, so it costs no round trip.  It never throws: a failed recording loses the answers rather than keeping the
// visitor from moving on or leaving onboarding state (the chosen org, typed email) for a later onboarding to pick up.
export async function finishOnboarding() {
  const responses = localStorageGet(LocalStorageKey.OnboardingHouseholdSurveyResponses);
  if (Object.keys(responses).length === 0) {
    clearOnboardingLocalStorage();
    return;
  }
  try {
    await recordSurveyResponses();
  } catch (e: any) {
    console.error(`Failed to record onboarding survey responses: ${e.message}`);
    clearOnboardingLocalStorage();
  }
}

export function setOnboardingHouseholdSurveyResponse(response: HouseholdSurveyResponses) {
  localStorageSet(LocalStorageKey.OnboardingHouseholdSurveyResponses, {
    ...localStorageGet(LocalStorageKey.OnboardingHouseholdSurveyResponses),
    ...response,
  });
}

export function setHouseholdOriginBasedOnOrgType(orgType: OrgType) {
  const origin = orgType === OrgType.School ? HouseholdOriginSource.School : HouseholdOriginSource.Church;
  setOnboardingHouseholdSurveyResponse({ origin });
}

export function getOnboardingAppStoreUrl(): undefined {
  // "Get Started" navigates to app store in mobile browsers
  // PROBLEM: Does not properly detect in-app for some Android devices
  // SOLUTION: Temporarily eliminate app store link for all Android devices
  // TODO(hewitt): When new Android Studio-based app releases, bring this back (assuming a reliable in-app indicator)
  // const appStoreUrl = isInsideMobileBrowser() && !isDebugOnboardingEnvironment() ? getAppStoreUrl() : undefined;
  // return isInsideMobileBrowser() &&
  //   !isDebugOnboardingEnvironment() &&
  //   getDevice() !== Device.Android &&
  //   getEnvironment() !== Environment.Dev &&
  //   (!iOSVersion || iOSVersion > 15) // iOS 15 WebView has problems with input focus, so allow mobile browser usage
  //   ? getAppStoreUrl()
  //   : undefined;
}
